import {useCallback} from 'react'
import {Button, SelectPanel, SkeletonBox} from '@primer/react'
import {ChevronRightIcon, CommentDiscussionIcon, GitBranchIcon, TriangleDownIcon, XIcon} from '@primer/octicons-react'
import {ReposSelector} from '@github-ui/repos-selector'
import {RefSelector} from '@github-ui/ref-selector'
import {queryForRepos} from '@github-ui/copilot-chat/components/TaskChatInputToolbar'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'

import {isRepoInScope} from '../../../types/task-composer-scope'
import styles from './RepoBranchPill.module.css'

// ─── TYPES ──────────────────────────────────────────────────────────────────

export interface RepoBranchPair {
  id: string
  repo: AgentDropdownRepository
  branch: string
}

// ─── REPO BRANCH PILL ───────────────────────────────────────────────────────

export function RepoBranchPill({
  pair,
  onRepoChange,
  onBranchChange,
  onRemove,
  removable,
  hideRepo,
  initialRepoNWO,
  onLoad,
  secondaryAction,
  branchSelectorDisabled,
  disabled,
  scopeOwner,
  repoPickerSubtitle,
  allowChat = false,
  onChatSelect,
}: {
  pair: RepoBranchPair
  onRepoChange: (pairId: string, repo: AgentDropdownRepository) => void
  onBranchChange: (pairId: string, branch: string) => void
  onRemove: (pairId: string) => void
  removable: boolean
  hideRepo?: boolean
  initialRepoNWO?: string
  onLoad?: (repositories: AgentDropdownRepository[]) => void
  branchSelectorDisabled?: boolean
  disabled?: boolean
  /** When present, filters the repo picker to repos owned by this login (plus public repos). */
  scopeOwner?: string
  /** Subtitle rendered under the repo picker title. */
  repoPickerSubtitle?: string
  allowChat?: boolean
  onChatSelect?: () => void
  secondaryAction?:
    | React.ReactElement<typeof SelectPanel.SecondaryActionButton>
    | React.ReactElement<typeof SelectPanel.SecondaryActionLink>
}) {
  const repoFullName = pair.repo.nameWithOwner

  const repositoryLoader = useCallback(
    async (query: string, defaultOwner?: string) => {
      const effectiveOwner = scopeOwner ?? defaultOwner
      const scopedQuery = scopeOwner && !query.includes('/') ? `${scopeOwner}/${query}` : query
      const repos = await queryForRepos(scopedQuery, effectiveOwner, initialRepoNWO)
      if (!scopeOwner) return repos
      return repos.filter(repo => isRepoInScope(repo, scopeOwner))
    },
    [initialRepoNWO, scopeOwner],
  )

  return (
    <span className={styles.pill}>
      {!hideRepo && (
        <>
          <ReposSelector<AgentDropdownRepository>
            repositoryLoader={repositoryLoader}
            selectionVariant="single"
            iconOnly={false}
            responsiveStyle="full"
            selectAllOption={allowChat}
            selectAllOptionPlacement="inline"
            selectAllOptionText="Chat"
            selectAllOptionLeadingVisual={CommentDiscussionIcon}
            buttonText={pair.repo.nameOnly}
            onSelect={repo => {
              if (repo) onRepoChange(pair.id, repo)
              else if (allowChat) onChatSelect?.()
            }}
            currentSelection={pair.repo}
            width="medium"
            additionalButtonProps={{
              'aria-label': `Repository: ${repoFullName}`,
              variant: 'invisible',
              size: 'small',
              className: styles.pillButton,
              disabled,
            }}
            onLoad={onLoad}
            loadingElement={<SkeletonBox width="80px" height="24px" />}
            secondaryAction={secondaryAction}
            subtitle={repoPickerSubtitle}
          />
          <ChevronRightIcon size={12} className={styles.pillChevron} />
        </>
      )}
      <RefSelector
        cacheKey={`env-strip-branch-${pair.id}`}
        canCreate={false}
        currentCommitish={pair.branch}
        defaultBranch={pair.repo.defaultBranch}
        owner={pair.repo.owner}
        repo={pair.repo.nameOnly}
        types={['branch']}
        selectedRefType="branch"
        closeOnSelect
        hideShowAll
        actionListHeadingText="Select a branch"
        onSelectItem={(branch: string) => onBranchChange(pair.id, branch)}
        disabled={disabled || branchSelectorDisabled}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderAnchor={(anchorProps: any) => (
          <Button
            {...anchorProps}
            aria-label={`Branch: ${pair.branch}`}
            variant="invisible"
            size="small"
            className={`${styles.pillButton} ${hideRepo ? styles.branchOnlyButton : ''}`}
            leadingVisual={hideRepo ? GitBranchIcon : undefined}
            trailingAction={hideRepo ? TriangleDownIcon : undefined}
          >
            {pair.branch}
          </Button>
        )}
      />
      {removable && (
        <button
          type="button"
          aria-label={`Remove ${repoFullName}`}
          onClick={() => onRemove(pair.id)}
          className={styles.pillRemove}
          disabled={disabled}
        >
          <XIcon size={12} />
        </button>
      )}
    </span>
  )
}
